import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './redux/store';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LocaleProvider } from './context/LocaleContext';
import './i18n'; // Initialize i18n
import Layout from './components/layout/Layout';
import ForgotPassword from './components/auth/ForgotPassword';
import PrivacyPolicy from './components/PrivacyPolicy';
import BillingDashboard from './components/billing/BillingDashboard';
import BillDetails from './components/billing/BillDetails';
import ClaimDetails from './components/billing/ClaimDetails';
import MonthEndClose from './components/billing/MonthEndClose';
import SnapshotViewer from './components/billing/SnapshotViewer';
import CreateEncounter from './components/encounters/CreateEncounter';
import EncounterList from './components/encounters/EncounterList';
import EncounterDetails from './components/encounters/EncounterDetails';
import EncounterTypeSelector from './components/encounters/EncounterTypeSelector';
import TriageBoard from './components/triage/TriageBoard';
import Login from './components/auth/Login';
import PatientAccountDeletion from './components/auth/PatientAccountDeletion';
import ModernDashboard from './components/ModernDashboard';
import PatientList from './components/patients/PatientList';
import PatientDetails from './components/patients/PatientDetails';
import PatientForm from './components/patients/PatientForm';
import AppointmentList from './components/appointments/AppointmentList';
import AppointmentDetails from './components/appointments/AppointmentDetails';
import CreateAppointment from './components/appointments/CreateAppointment';


import FacilityForm from './components/facilities/FacilityForm';
import FacilityBranding from './components/facilities/FacilityBranding';
import UserManagement from './components/admin/UserManagement';
import PrivateRoute from './components/auth/PrivateRoute';
import PharmacyList from './components/pharmacy/PharmacyList';
import CreatePharmacy from './components/pharmacy/CreatePharmacy';
import PrescriptionsList from './components/prescriptions/PrescriptionsList';
import MedicalRecords from './components/records/MedicalRecords';
import MyAccount from './components/account/MyAccount.jsx';
import PersonalizedDashboard from './components/Dashboard/PersonalizedDashboard';
// import MessagingDashboard from './components/messaging/MessagingDashboard';
import ThreadDetails from './components/messaging/ThreadDetails';
import CreateThread from './components/messaging/CreateThread';

import FacilityGroups from './components/admin/FacilityGroups';
import Macros from './components/admin/Macros';
import ComplianceAuditReport from './components/admin/ComplianceAuditReport';
import LabTestHarness from './components/Testing/LabTestHarness';
import FeatureList from './components/admin/FeatureList';
import FeatureConfiguration from './components/admin/FeatureConfiguration';
import CalendarPage from './components/calendar/CalendarPage';
import AuditReports from './components/calendar/AuditReports';
import CareCoordination from './components/CareCoordination/CareCoordination';
import BIReporting from './components/BIReporting';
import FacilityBenchmarks from './components/BIReporting/FacilityBenchmarks';
import PredictiveAnalytics from './components/BIReporting/PredictiveAnalytics';
import CareGaps from './components/BIReporting/CareGaps';
import ThirdPartyIntegrations from './components/admin/ThirdPartyIntegrations';
import APIIntegrations from './components/admin/APIIntegrations.jsx';
import HISPConnection from './components/admin/HISPConnection';
import { checkInRoutes } from './routes/checkInRoutes';
import Forms from './components/admin/Forms';
import FormBuilder from './components/admin/FormBuilder';
import { procedureRoutes } from './routes/procedureRoutes';
import { inpatientRoutes } from './routes/inpatientRoutes';
import { oncologyRoutes } from './routes/oncologyRoutes';
import Codes from './components/codes/Codes';
import LabReportsDemo from './components/LabReports/LabReportsDemo';
import ClinicalDataCodesPage from './components/codes/ClinicalDataCodesPage';
import ClinicalTermsCodesPage from './components/codes/ClinicalTermsCodesPage';
import DiagnosisCodesPage from './components/codes/DiagnosisCodesPage';
import LocalUseCodesPage from './components/codes/LocalUseCodesPage';
import MedicationCodingCodesPage from './components/codes/MedicationCodingCodesPage';
import ImmunizationCodesPage from './components/codes/ImmunizationCodesPage';
import TaxonomyCodesPage from './components/codes/TaxonomyCodesPage';
import TransmissionLog from './components/transmission/TransmissionLog';
import LocaleDemo from './components/LocaleDemo';
import AllergiesMetadataViewer from './components/allergies/AllergiesMetadataViewer';
import InpatientWorkflowConfig from './components/admin/InpatientWorkflowConfig';
import ReportConfiguration from './components/admin/ReportConfiguration';

import DicomPreloader from './components/radiology/DicomPreloader';
import SurgicalFacilities from './components/surgical-facilities';
import SurgeryDashboard from './components/surgical-facilities/SurgeryDashboard';
import './styles/components.css';
import { AuthProvider } from './context/AuthContext';
import CacheProvider from './components/common/CacheProvider';
import { MacrosProvider } from './context/MacrosContext';
import MailboxLayout from './components/messaging/MailboxLayout';
import { UserRole } from './types/enums';
import JoinTelehealthSession from './components/telehealth/JoinTelehealthSession';

// Create a custom theme with proper navigation styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#708bff',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(112, 139, 255, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(112, 139, 255, 0.12)',
            },
            '& .MuiListItemIcon-root': {
              color: '#fff',
            },
            '& .MuiListItemText-primary': {
              color: '#fff',
              fontWeight: 500,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '& .MuiListItemIcon-root': {
              color: '#708bff',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.6)',
          minWidth: 40,
          '.Mui-selected &': {
            color: '#fff',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'rgba(0, 0, 0, 0.87)',
          fontSize: '0.875rem',
          '.Mui-selected &': {
            color: '#fff',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'rgba(0, 0, 0, 0.87)',
            borderBottom: '2px solid #e0e0e0',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'rgba(0, 0, 0, 0.87)',
          borderBottom: '2px solid #e0e0e0',
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        body: {
          textAlign: 'center',
          fontSize: '0.875rem',
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 400,
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocaleProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <AuthProvider>
                <CacheProvider>
                  <MacrosProvider>
                    <Router>
                      <Suspense fallback={<div>Loading...</div>}>
                        {/* Preload DICOM viewer components in the background for better performance */}
                        <DicomPreloader 
                          enabled={true}
                          delay={2000}
                        />
                        <Routes>
                          {/* Make login the default route */}
                          <Route path="/" element={<Navigate to="/login" replace />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/patient-account-deletion" element={<PatientAccountDeletion />} />
                        
                          {/* Patient Check-in routes */}
                          {checkInRoutes}
                        
                          {/* Update Patient Portal route to show a message or redirect */}
                          <Route path="/patient/*" element={
                            <Navigate to="/login" replace />
                          } />
                        
                          {/* Main app routes */}
                          <Route path="/" element={<Layout />}>
                              {/* Protected routes */}
                              <Route element={<PrivateRoute />}>
                                {/* Basic routes that any authenticated user can access */}
                                <Route path="dashboard" element={<PersonalizedDashboard />} />
                                <Route path="revenue/dashboard" element={<ModernDashboard />} />
                                <Route path="revenue/surgery-dashboard" element={<SurgeryDashboard />} />
                                <Route path="locale-demo" element={<LocaleDemo />} />
                                <Route path="lab-reports-demo" element={<LabReportsDemo />} />                         
                              
                                {/* Admin and Facility Admin routes */}
                                <Route element={<PrivateRoute requireAdmin={true} requireFacilityAdmin={true} />}>
                                  <Route path="facilities/branding" element={<FacilityBranding />} />
                                </Route>

                                {/* Billing routes with specific role access */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.BILLING_SPECIALIST, UserRole.FACILITY_ADMIN]} />}>
                                  <Route path="billing" element={<BillingDashboard />} />
                                  <Route path="billing/month-end-close" element={<MonthEndClose />} />
                                  <Route path="billing/snapshots/:snapshotId" element={<SnapshotViewer />} />
                                  <Route path="billing/claims/:claimId" element={<ClaimDetails />} />
                                  <Route path="billing/:billId" element={<BillDetails />} />
                                  <Route path="admin/billing" element={<BillingDashboard />} />
                                  <Route path="admin/billing/month-end-close" element={<MonthEndClose />} />
                                  <Route path="admin/billing/snapshots/:snapshotId" element={<SnapshotViewer />} />
                                  <Route path="admin/billing/claims/:claimId" element={<ClaimDetails />} />
                                  <Route path="admin/billing/:billId" element={<BillDetails />} />
                                </Route>

                                {/* Admin and Facility Admin routes */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.FACILITY_ADMIN]} />}>
                                  <Route path="admin" element={<Navigate to="/admin/users" replace />} />
                                  <Route path="admin/facilities/new" element={<FacilityForm />} />
                                  <Route path="admin/facilities/:id" element={<FacilityForm />} />
                                  <Route path="admin/users" element={<UserManagement />} />

                                  <Route path="admin/facility-groups" element={<FacilityGroups />} />
                                  <Route path="admin/pharmacies" element={<PharmacyList />} />
                                  <Route path="admin/pharmacies/create" element={<CreatePharmacy />} />
                                  <Route path="admin/pharmacies/:id/edit" element={<CreatePharmacy />} />
                                  <Route path="admin/features" element={<FeatureList />} />
                                  <Route path="admin/features/:featureId/configure" element={<FeatureConfiguration />} />
                                  <Route path="admin/third-party-integrations" element={<ThirdPartyIntegrations />} />
                                  <Route path="admin/api-integrations" element={<APIIntegrations />} />
                                  <Route path="admin/hisp-connection" element={<HISPConnection />} />
                                  <Route path="admin/forms" element={<Forms />} />
                                  <Route path="admin/forms/new" element={<FormBuilder />} />
                                  <Route path="admin/forms/:formId/edit" element={<FormBuilder />} />
                                  <Route path="admin/codes" element={<Codes />} />
                                  <Route path="admin/codes/clinical-data" element={<ClinicalDataCodesPage />} />
                                  <Route path="admin/codes/clinical-terms" element={<ClinicalTermsCodesPage />} />
                                  <Route path="admin/codes/diagnosis-codes" element={<DiagnosisCodesPage />} />
                                  <Route path="admin/codes/local-use" element={<LocalUseCodesPage />} />
                                  <Route path="admin/codes/medication-coding" element={<MedicationCodingCodesPage />} />
                                  <Route path="admin/codes/immunization-codes" element={<ImmunizationCodesPage />} />
                                  <Route path="admin/codes/taxonomy-codes" element={<TaxonomyCodesPage />} />
                                  <Route path="admin/macros" element={<Macros />} />
                                  <Route path="admin/compliance-audit" element={<ComplianceAuditReport />} />
                                  <Route path="admin/lab-test-harness" element={<LabTestHarness />} />
                                  <Route path="admin/transmission-log" element={<TransmissionLog />} />
                                  <Route path="admin/allergies-metadata" element={<AllergiesMetadataViewer />} />
                                  <Route path="admin/inpatient-workflow" element={<InpatientWorkflowConfig />} />
                                  <Route path="admin/reports" element={<ReportConfiguration />} />

                               
                                </Route>

                                {/* Surgical Facilities routes */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.DOCTOR, UserRole.NURSE, UserRole.FACILITY_ADMIN]} />}>
                                  <Route path="surgical-facilities" element={<SurgicalFacilities />} />
                                </Route>

                                {/* Oncology routes */}
                                {oncologyRoutes}

                                {/* Procedures routes */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.DOCTOR, UserRole.NURSE]} />}>
                                  <Route path="procedures/*" element={procedureRoutes} />
                                </Route>

                                {/* Inpatient routes */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.DOCTOR, UserRole.NURSE, UserRole.FACILITY_ADMIN]} />}>
                                  {inpatientRoutes}
                                </Route>

                                {/* Prescriptions routes */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.DOCTOR, UserRole.NURSE, UserRole.FACILITY_ADMIN]} />}>
                                  <Route path="prescriptions" element={<PrescriptionsList />} />
                                </Route>

                                {/* Other authenticated routes */}
                                <Route path="patients" element={<PatientList />} />
                                <Route path="patients/:id" element={<PatientDetails />} />
                                {/* Encounter routes - unified CreateEncounter handles all encounter types */}
                                <Route path="patients/:patientId/encounters/new" element={<CreateEncounter />} />
                                <Route path="patients/:patientId/encounters/select-type" element={<EncounterTypeSelector />} />
                                <Route path="patients/:patientId/encounters/:encounterId" element={<EncounterDetails />} />
                                <Route path="patients/:patientId/encounters" element={<EncounterList />} />
                                <Route path="patients/new" element={<PatientForm />} />
                                <Route path="encounters" element={<EncounterList />} />
                                <Route path="encounters/:encounterId" element={<EncounterDetails />} />
                                <Route path="triage-board" element={<TriageBoard />} />
                                <Route path="appointments" element={<AppointmentList />} />
                                <Route path="appointments/new" element={<CreateAppointment />} />
                                <Route path="appointments/:id" element={<AppointmentDetails />} />
                                <Route path="calendar" element={<CalendarPage />} />
                                
                                {/* Calendar Audit Reports - Admin and Facility Admin only */}
                                <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.FACILITY_ADMIN]} />}>
                                  <Route path="calendar/audit-reports" element={<AuditReports />} />
                                </Route>
                                <Route path="my-account" element={<MyAccount />} />
                                <Route path="records" element={<MedicalRecords />} />
                                <Route path="messaging" element={<MailboxLayout />} />
                                <Route path="messaging/create" element={<CreateThread />} />
                                <Route path="messaging/:threadId" element={<ThreadDetails />} />
                                <Route path="carecoordination" element={<CareCoordination />} />
                                <Route path="bi-reporting/compliance" element={<BIReporting />} />
                                <Route path="bi-reporting/predictive" element={<PredictiveAnalytics />} />
                                <Route path="bi-reporting/care-gaps" element={<CareGaps />} />
                                <Route path="bi-reporting/benchmarks" element={<FacilityBenchmarks />} />
                                <Route path="/join-telehealth/:sessionId" element={<JoinTelehealthSession />} />
                              </Route>
                            </Route>
                        </Routes>
                      </Suspense>
                    </Router>
                  </MacrosProvider>
                </CacheProvider>
              </AuthProvider>
            </LocalizationProvider>
          </LocaleProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
